// Hand-rolled wrappers around postRequest/deleteRequest for the suppression +
// saved-filter endpoints. The shared apiFetch already handles JWT, /outbound
// prefix, and envelope unwrapping; we just keep the call sites typed and the
// request bodies in one place.

import { deleteRequest, patchRequest, postRequest } from '../../services/api'
import {
  TAnomaliesSuppressionScope,
  TAnomaliesV2UserFilterScope,
} from '../shared/types'

export interface CreateSuppressionInput {
  advertiserDomain: string
  expiresAt: string | null
  reason?: string | null
  // null/omitted = all daily alerts; a metric = that metric's daily alerts
  // only; 'rejections' = hide from the Monthly Rejection Review.
  primaryMetric?: TAnomaliesSuppressionScope | null
}

export const createSuppression = (input: CreateSuppressionInput) =>
  postRequest('/anomalies/v2/suppressions', {
    advertiser_domain: input.advertiserDomain,
    expires_at: input.expiresAt,
    reason: input.reason ?? null,
    primary_metric: input.primaryMetric ?? null,
  })

export const deleteSuppression = (uuid: string) =>
  deleteRequest(`/anomalies/v2/suppressions/${encodeURIComponent(uuid)}`)

// ---------------------------------------------------------------------------
// Saved filter presets
// ---------------------------------------------------------------------------

export type UserFilterValues = Record<string, string | number>

export interface CreateUserFilterInput {
  scope: TAnomaliesV2UserFilterScope
  name: string
  isDefault: boolean
  values: UserFilterValues
}

export const createUserFilter = (input: CreateUserFilterInput) =>
  postRequest('/anomalies/v2/user-filters', input)

// Partial update. Pass any subset of { name, isDefault, values } — the backend
// enforces "at least one field". Setting isDefault: true auto-clears the
// previous default in this scope; the FE doesn't need to PATCH it first.
export interface UpdateUserFilterInput {
  name?: string
  isDefault?: boolean
  values?: UserFilterValues
}

export const updateUserFilter = (uuid: string, input: UpdateUserFilterInput) =>
  patchRequest(
    `/anomalies/v2/user-filters/${encodeURIComponent(uuid)}`,
    input
  )

export const deleteUserFilter = (uuid: string) =>
  deleteRequest(`/anomalies/v2/user-filters/${encodeURIComponent(uuid)}`)
